// Dashboard: at-a-glance metrics across the workflow. Read-only; it writes
// nothing.
//
// REBUILT 2026-09-14 (owner). Three things changed and they are worth stating
// plainly, because each replaced something that still works:
//
//  1. THE FILTERS ARE THE REPORTING SCREEN'S, and they are not on this screen.
//     The Window / Company / Vendor / Measure row is gone; a Filters button in
//     the Analytics header (`AnalyticsScreen.tsx`) edits the same fifteen
//     criteria the Reporting form asks, and hands them here as a prop. The
//     owner's words: abandon the ones currently there "in favor of the ones
//     from the Reporting screen".
//  2. THE KPI BLOCK IS INDEPENDENT OF THEM, deliberately, and says so on the
//     page. It reads §16.9, which takes no parameters at all. A reader who has
//     narrowed the panels to one vendor still needs the unnarrowed state of the
//     queue above them.
//  3. THE PANELS ARE FIVE SECTIONS OF FOUR, one measure per panel, rather than
//     a single flow of whatever each aggregate happened to return. The same
//     four questions — how many, how much, how long, how often denied — are
//     asked of each of Step, Vendor and Company, which is what lets a reader
//     compare across the rows.
//
//  4. A BAR IS A FILTER CONTROL (owner, 2026-09-15). Clicking one applies its
//     own value to the whole screen and clicking it again takes it off, so the
//     common narrowing never needs the popover at all.
//  5. A ROW IN THE LISTING OPENS THE INVOICE, read-only (owner, 2026-09-14) —
//     built in a concurrent session against the previous dashboard and carried
//     across in the merge, unchanged but for the rows it now hangs off.
//
// THE CRITERIA COME IN AS PROPS AND GO OUT THROUGH `onCriteriaChange`, and this
// screen holds none of that state itself. The Filters button lives in the
// Analytics header today and is moving to the app shell's own header as an icon
// (owner, 2026-09-15); when it does, the state moves up with it and nothing here
// changes but where the two props come from.
//
// Every panel is an aggregation endpoint (`docs/app.md` → Analytics): each call
// is already scoped by the API to the rows this user may see, and each carries
// an `as_of` stamp. The client only labels and orders what comes back.
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Body1,
  Button,
  Caption1,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import { errorMessage } from '../api/errors'
import type { Session } from '../lib/identity'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { useClickableRowClass, rowActivationProps } from '../components/tableDensity'
import type {
  AnalyticsByDimension,
  AnalyticsCorrections,
  AnalyticsDimensionRow,
  AnalyticsKpis,
  AnalyticsSpendByCode,
  AnalyticsSpendByEntity,
} from '../api/types'
import type { Invoice } from '../lib/types'
import {
  loadAnalyticsByDimension,
  loadAnalyticsCorrections,
  loadAnalyticsKpis,
  loadAnalyticsSpendByCode,
  loadAnalyticsSpendByEntity,
  loadDashboardInvoices,
} from '../lib/data'
import { STEP_LABEL, TIMELINE_NODES, flagLabel } from '../lib/constants'
import {
  emptyCriteria,
  statusSelectionForNode,
  toAnalyticsParams,
  toInvoiceListParams,
  toggleValues,
  type Criteria,
} from '../lib/criteria'
import { correctionFieldLabel } from '../lib/metrics'
import { formatCurrency, formatDate, ageInDays } from '../lib/format'
import { SECTION_COLORS } from '../lib/theme'
import { PanelCard, KpiCard, HBarList, type BarRowData } from '../components/DashboardCharts'
import { AgeProgressBar } from '../components/AgeProgressBar'
import { StatusBadge, AgePill } from '../components/Badges'
import { Loading, ErrorBanner } from '../components/States'

/** How many invoices the listing shows at a time. */
const PAGE_SIZE = 25

/** How many bars a ranking panel draws. Enough to read, short enough to scan. */
const TOP_N = 10

/** A median of flags per invoice: whole when it is, one place when it falls between two. */
function flagMedian(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1)
}

const weekLabel = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

/**
 * MEDIAN FLAGS PER INVOICE, BY WEEK (owner, 2026-10-16): one bar per week
 * received, OLDEST FIRST, the latest ten weeks in the view — the top-ten rule
 * the other panels keep, applied to time rather than rank.
 */
function weeklyMedianBars(
  rows: readonly { week_start: string; median: number; invoice_count: number }[],
  color: string,
): BarRowData[] {
  return rows.slice(-TOP_N).map((row) => ({
    key: row.week_start,
    label: `Week of ${weekLabel.format(new Date(`${row.week_start}T00:00:00Z`))}`,
    total: row.median,
    display: flagMedian(row.median),
    segments: [{ color, value: row.median }],
    title: `${flagMedian(row.median)} flags per invoice, median of ${row.invoice_count} invoice${row.invoice_count === 1 ? '' : 's'}`,
  }))
}

/** The server's stand-in key for a row whose dimension column is null. Not a
    filterable value — "vendor is (none)" is not a question the contract asks. */
const NO_KEY = '(none)'

const compactUsd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
})

/** Node key -> the label the timeline prints. A key with no node keeps itself. */
const NODE_LABEL = new Map<string, string>(TIMELINE_NODES.map((node) => [node.key, node.label]))

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: '24px', paddingTop: '8px' },
  sub: { color: tokens.colorNeutralForeground3 },
  // THE KPI BLOCK IS A BLOCK (owner, 2026-09-14: "put them in a block or
  // something, labeled as such"). Its own surface, so the eye reads it as a
  // separate thing from the filtered panels below — which is exactly what it is.
  //
  // ITS HEADING SITS OUTSIDE THE BORDER (owner, 2026-09-15: "This Week at a
  // Glance sits inside the border line, but every other title sits outside the
  // border line"). The heading was in here for a day, which made it the one
  // section title on the page indented from the five below it. It is now an
  // ordinary `sectionHead` above an ordinary surface, so this rule only draws
  // the box.
  kpiBlock: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: '16px 20px',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
  },
  // The bar spans the whole block, under the cards, with a rule between them —
  // it summarizes the four rather than standing beside them as a fifth.
  kpiBar: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  section: { display: 'flex', flexDirection: 'column', gap: '12px' },
  sectionHead: { display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' },
  // A SECTION'S FOUR PANELS ARE A 2x2 BLOCK, not a row of four.
  //
  // `auto-fit` was the first cut and it was wrong here: the app shell caps the
  // content column at roughly 1,230px, so auto-fit with a 320px minimum lands
  // on THREE tracks at every desktop width — and a section of four then renders
  // three across with the fourth orphaned on its own row, which reads as a
  // missing panel rather than as a wrap. Forcing four across instead would give
  // ~290px panels, and `HBarList` spends 132px of that on the label and 64px on
  // the value, leaving under 100px of actual bar.
  //
  // Two fixed columns divide four — and six, since handling time joined the
  // per-dimension block — exactly, at ~600px each: the bar is the widest part
  // of the row again, and a vendor name has room to read. One column below
  // 900px. `minmax(0, …)`, never `1fr`, or a long label forces its column wider
  // and the block stops lining up.
  quad: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: '16px',
    '@media (min-width: 900px)': {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  listTable: { tableLayout: 'fixed', width: '100%' },
  // THE TABLE STAYS MOUNTED WHILE A PAGE LOADS (owner, 2026-09-15: "everything
  // falls to the bottom before moving up again, and it is a bit disorienting").
  // The rows on screen are the PREVIOUS page until the next one arrives, so the
  // panel keeps its height and nothing below it moves; this is the only signal
  // that a fetch is in flight, and the pager's buttons disable alongside it.
  listLoading: { opacity: 0.45, transition: 'opacity 120ms ease-out' },
  right: { textAlign: 'right' },
  rightHeader: { '& .fui-TableHeaderCell__button': { justifyContent: 'flex-end' } },
  ellipsis: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  pager: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '12px',
    color: tokens.colorNeutralForeground3,
  },
  empty: { padding: '48px 0', color: tokens.colorNeutralForeground3 },
})

/** The three dimensional breakdowns, plus the reads that are not one. */
interface DashData {
  step: AnalyticsByDimension | null
  vendor: AnalyticsByDimension | null
  company: AnalyticsByDimension | null
  accounts: AnalyticsSpendByCode | null
  departments: AnalyticsSpendByCode | null
  codes: AnalyticsSpendByCode | null
  entities: AnalyticsSpendByEntity | null
  corrections: AnalyticsCorrections | null
}

interface Props {
  session: Session
  refreshNonce?: number
  /** The Filters button's criteria, owned by `AnalyticsScreen.tsx` today and by
      the app shell once the button moves there (owner, 2026-09-15). */
  criteria?: Criteria
  /** How a bar click narrows the screen. Absent means the bars are read-only. */
  onCriteriaChange?: (next: Criteria) => void
  /** From `App.tsx`, through `AnalyticsScreen`: where this invoice is being
      worked, when the viewer holds that step. Drives the modal's "Go To <Step>"
      button; absent or `null` means no button. */
  resolveStep?: (invoice: Invoice) => { label: string; onGo: () => void } | null
}

/** Rows sorted by one measure, capped, and turned into bars of one colour. */
function bars(
  rows: readonly AnalyticsDimensionRow[],
  measure: (row: AnalyticsDimensionRow) => number,
  display: (value: number) => string,
  color: string,
  label: (row: AnalyticsDimensionRow) => string,
): BarRowData[] {
  return [...rows]
    .filter((row) => measure(row) > 0)
    .sort((a, b) => measure(b) - measure(a))
    .slice(0, TOP_N)
    .map((row) => ({
      key: row.key,
      label: label(row),
      total: measure(row),
      display: display(measure(row)),
      segments: [{ color, value: measure(row) }],
      title: `${label(row)}: ${display(measure(row))}`,
    }))
}

export function DashboardScreen({
  session,
  refreshNonce = 0,
  criteria = emptyCriteria,
  onCriteriaChange,
  resolveStep,
}: Props) {
  const s = useStyles()
  const [kpis, setKpis] = useState<AnalyticsKpis | null>(null)
  // A FAILED KPI READ SAYS SO (code review H-07, 2026-10-17). It used to leave
  // `kpis` null, which drew "Loading…" for ever. Retry re-reads it alone.
  const [kpiError, setKpiError] = useState<string | null>(null)
  const [kpiRetry, setKpiRetry] = useState(0)
  const [data, setData] = useState<DashData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // A ROW OPENS THE INVOICE (owner, 2026-09-14: "I should be able to click an
  // invoice row and get the invoice modal to appear on this screen. I am fine
  // with leaving it read-only from here").
  //
  // READ-ONLY IS NOT A CHOICE THIS SCREEN MAKES, it is the only thing it can
  // offer: `InvoiceDetailModal` edits when it is handed an `editStep`, and
  // there is no step to hand it here — the Dashboard lists invoices at every
  // status at once, including terminal ones, so there is no single gate the
  // viewer is acting at. Same call shape Archives uses, which is the app's
  // other read-only lookup.
  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  // THE SOURCE PAGES, VIEW-ONLY (owner, 2026-10-01: "These screens are
  // view-only; let's let users view the source material"). Every read here runs
  // under the Dashboard lens, which carries document access for the Dashboard
  // seats' companies — so a seat is the whole answer, whatever else is held.
  const showPdf = session.analyticsCompanies.dashboard.length > 0
  const clickableRow = useClickableRowClass()

  const analyticsParams = useMemo(() => toAnalyticsParams(criteria), [criteria])
  const listParams = useMemo(() => toInvoiceListParams(criteria), [criteria])

  // THE KPI BLOCK RELOADS ON A REFRESH, NOT ON A FILTER CHANGE. It takes no
  // criteria, so re-reading it when they change would be a request that could
  // not return anything different.
  useEffect(() => {
    let cancelled = false
    setKpiError(null)
    loadAnalyticsKpis()
      .then((next) => {
        if (!cancelled) setKpis(next)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setKpis(null)
        setKpiError(errorMessage(e))
      })
    return () => {
      cancelled = true
    }
  }, [refreshNonce, kpiRetry])

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    // EVERY PANEL FOR EVERY DASHBOARD SEAT (owner, 2026-10-17 — code review
    // H-04: "The user authorized for Analytics > Dashboard should see all
    // content there for the Company corresponding to his role."). Entities by
    // Spend needed Import Control and the correction and flag panels an
    // internal role, in the browser only; the server never asked. Row-level
    // security keeps each seat to its own companies.
    const [step, vendor, company, accounts, departments, codes, entities, corrections] =
      await Promise.allSettled([
        loadAnalyticsByDimension('step', analyticsParams),
        loadAnalyticsByDimension('vendor', analyticsParams),
        loadAnalyticsByDimension('company', analyticsParams),
        loadAnalyticsSpendByCode('account', analyticsParams),
        loadAnalyticsSpendByCode('department', analyticsParams),
        loadAnalyticsSpendByCode('code', analyticsParams),
        loadAnalyticsSpendByEntity(analyticsParams),
        loadAnalyticsCorrections(analyticsParams),
      ])
    // The three breakdowns are the screen. If none of them answered there is
    // nothing to draw, and an error banner is more honest than five empty
    // panels; a single failed panel just does not render.
    if (
      step.status === 'rejected' &&
      vendor.status === 'rejected' &&
      company.status === 'rejected'
    ) {
      setError(errorMessage(step.reason))
      setData(null)
    } else {
      const settled = <T,>(result: PromiseSettledResult<T>): T | null =>
        result.status === 'fulfilled' ? result.value : null
      setData({
        step: settled(step),
        vendor: settled(vendor),
        company: settled(company),
        accounts: settled(accounts),
        departments: settled(departments),
        codes: settled(codes),
        entities: settled(entities),
        corrections: settled(corrections),
      })
    }
    setLoading(false)
  }, [analyticsParams])

  useEffect(() => {
    void reload()
  }, [reload, refreshNonce])

  // ---- The listing ------------------------------------------------------------
  // Paged SERVER-SIDE. "All applicable invoices" is an unbounded set — every
  // status the filter did not exclude, archives included — so the screen asks
  // for a page at a time and keeps the cursors it has been handed. Cursors are
  // opaque; the back button walks the stack rather than decoding one.
  const [rows, setRows] = useState<Invoice[] | null>(null)
  const [total, setTotal] = useState<number | null>(null)
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])
  const [pageIndex, setPageIndex] = useState(0)
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined)
  // Separate from `rows === null`, deliberately: `null` means "nothing has ever
  // loaded" and draws the empty frame, this means "a page is on its way and the
  // one on screen is the last one". Collapsing the two is what made Previous
  // and Next drop the table to zero height and bounce the page.
  const [pageLoading, setPageLoading] = useState(true)

  // A filter change invalidates every cursor already issued: they were issued
  // against the old set. Back to page one.
  useEffect(() => {
    setCursors([undefined])
    setPageIndex(0)
  }, [listParams])

  useEffect(() => {
    let cancelled = false
    setPageLoading(true)
    loadDashboardInvoices(listParams, cursors[pageIndex], PAGE_SIZE)
      .then((page) => {
        if (cancelled) return
        setRows(page.rows)
        setTotal(page.total ?? null)
        setNextCursor(page.cursor)
        setPageLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setRows([])
        setNextCursor(undefined)
        setPageLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [listParams, cursors, pageIndex, refreshNonce])

  /**
   * How many pages there are, from the server's total.
   *
   * `null` when the server sent no total — the pager then says which page it is
   * on and nothing about how many there are, rather than guessing a denominator.
   */
  const pageCount = useMemo(
    () => (total === null ? null : Math.max(1, Math.ceil(total / PAGE_SIZE))),
    [total],
  )

  const showMore = useCallback(() => {
    if (nextCursor === undefined) return
    setCursors((prev) => {
      // The cursor for the page AFTER the one on screen. Appended once: paging
      // forward, back, then forward again must not push a duplicate.
      const next = [...prev]
      next[pageIndex + 1] = nextCursor
      return next
    })
    setPageIndex((prev) => prev + 1)
  }, [nextCursor, pageIndex])

  // ---- Clicking a bar narrows the screen ---------------------------------------
  /**
   * Toggle one bar's value on the criterion it belongs to (owner, 2026-09-15:
   * *"would it be possible to click a bar and have that value be applied to the
   * screen as a filter?"*).
   *
   * Which criterion a bar belongs to is a property of the PANEL, not of the
   * value, so each caller names it. Three panels have no criterion to belong to
   * and are deliberately not clickable: **Entities by Spend** (there is no
   * entity filter on the wire), **Corrections Per Field** (no field filter), and
   * every bar keyed `(none)` — a row with no vendor or no company cannot be
   * expressed as "vendor is …", and applying an empty id would silently return
   * nothing.
   */
  const applyFilter = useCallback(
    (key: keyof Criteria & ('companies' | 'vendors' | 'accounts' | 'departments' | 'codes' | 'statuses'),
     values: readonly string[]): void => {
      if (onCriteriaChange === undefined) return
      onCriteriaChange({ ...criteria, [key]: toggleValues(criteria[key], values) })
    },
    [criteria, onCriteriaChange],
  )

  /** A bar's `onSelect`, or `undefined` where the panel cannot filter. */
  const selectFor = useCallback(
    (
      key: 'companies' | 'vendors' | 'accounts' | 'departments' | 'codes' | 'statuses',
      expand: (barKey: string) => string[] = (barKey) => [barKey],
    ): ((barKey: string) => void) | undefined => {
      if (onCriteriaChange === undefined) return undefined
      return (barKey: string) => {
        if (barKey === NO_KEY) return
        applyFilter(key, expand(barKey))
      }
    },
    [applyFilter, onCriteriaChange],
  )

  // ---- Panels ------------------------------------------------------------------
  const stepLabel = useCallback(
    (row: AnalyticsDimensionRow) => NODE_LABEL.get(row.key) ?? STEP_LABEL[row.key] ?? row.key,
    [],
  )
  const plainLabel = useCallback((row: AnalyticsDimensionRow) => row.label, [])

  const count = (row: AnalyticsDimensionRow) => row.invoice_count
  const spend = (row: AnalyticsDimensionRow) => row.total
  // A null median is "nothing has finished here yet". It is NOT zero, and
  // treating it as zero would draw a bar claiming instant turnaround; `bars`
  // drops a row measuring 0, so a null row simply does not appear.
  const days = (row: AnalyticsDimensionRow) => row.median_days ?? 0
  const denials = (row: AnalyticsDimensionRow) => row.denial_count
  const holds = (row: AnalyticsDimensionRow) => row.hold_count
  // HANDLING TIME IS NOT MEDIAN DAYS. Days are how long the invoice SAT;
  // seconds are how long a person WORKED on it. Null is "never measured" — no
  // view session exists for the row, which is true of everything from before
  // the measure shipped — and `bars` drops a zero, so those rows do not draw.
  const handling = (row: AnalyticsDimensionRow) => row.median_handling_seconds ?? 0
  const touches = (row: AnalyticsDimensionRow) => row.touch_count

  const whole = (value: number) => String(Math.round(value))
  const money = (value: number) => compactUsd.format(value)
  const dayCount = (value: number) => `${value} d`
  /** `45 s` under a minute, `2m 05s` above it — a median nobody reads as exact. */
  const seconds = (value: number) => {
    const total = Math.round(value)
    if (total < 60) return `${total} s`
    return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`
  }

  /**
   * One section of six: the same six measures asked of one dimension, all in
   * that section's own colour (owner, 2026-09-15; handling time and times
   * opened added 2026-09-20).
   *
   * MEDIAN DAYS AND MEDIAN HANDLING TIME SIT SIDE BY SIDE ON PURPOSE. A step
   * can be slow in days and quick in seconds — that is a queue problem, not a
   * tool problem — and the pair is the only way to tell those apart. Times
   * opened is the third leg: forty seconds across six openings is a different
   * story from forty across one.
   *
   * NO HINT LINES. Each panel used to carry "top 10 · invoices matching the
   * filters now" and its three siblings; the owner cut all of them. The title
   * says what the panel is, and a caption repeating the filter state under every
   * one of twenty panels was noise, not help.
   */
  const quad = (
    title: string,
    breakdown: AnalyticsByDimension | null,
    label: (row: AnalyticsDimensionRow) => string,
    noun: string,
    color: string,
    onSelect: ((barKey: string) => void) | undefined,
  ) => {
    if (breakdown === null) return null
    const rows = breakdown.rows
    const panel = (
      panelTitle: string,
      measure: (row: AnalyticsDimensionRow) => number,
      display: (value: number) => string,
    ) => {
      const drawn = bars(rows, measure, display, color, label)
      return (
        <PanelCard title={panelTitle}>
          {drawn.length === 0 ? (
            <Caption1 className={s.sub}>Nothing to show for these filters.</Caption1>
          ) : (
            <HBarList rows={drawn} onSelect={onSelect} />
          )}
        </PanelCard>
      )
    }
    return (
      <div className={s.section}>
        <div className={s.sectionHead}>
          <Subtitle2>{title}</Subtitle2>
          {/* WHEN THE SERVER LEFT ROWS OUT, SAY SO (code review H-01). It keeps
              the top 50 on each measure, so every panel's top ten is still
              right; the caption says the list behind them is not every one. */}
          {breakdown.truncated && (
            <Caption1 className={s.sub}>
              {`Some ${noun === 'Company' ? 'companies' : `${noun.toLowerCase()}s`} with small numbers on every measure are not shown.`}
            </Caption1>
          )}
        </div>
        {/* THE TWO DURATIONS SHARE A ROW, and the order exists for that. The
            grid is two columns, so appending the new panels would have left
            Median Days and Median Handling Time diagonal from each other —
            the one pairing a reader has to make. Volume and money first, the
            two durations second, the two event counts third. */}
        <div className={s.quad}>
          {panel(`Invoices Per ${noun}`, count, whole)}
          {panel(`Spend Per ${noun}`, spend, money)}
          {panel(`Median Days Per ${noun}`, days, dayCount)}
          {panel(`Median Handling Time Per ${noun}`, handling, seconds)}
          {panel(`Times Opened Per ${noun}`, touches, whole)}
          {panel(`Denials Per ${noun}`, denials, whole)}
        </div>
      </div>
    )
  }

  const spendPanel = (
    title: string,
    breakdown: AnalyticsSpendByCode | null,
    onSelect: ((barKey: string) => void) | undefined,
  ) => {
    if (breakdown === null) return null
    const drawn: BarRowData[] = [...breakdown.rows]
      .filter((row) => row.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, TOP_N)
      .map((row) => ({
        key: row.key,
        label: row.label,
        total: row.total,
        display: money(row.total),
        segments: [{ color: SECTION_COLORS.spend, value: row.total }],
        title: `${row.label}: ${money(row.total)} across ${row.count} invoice${row.count === 1 ? '' : 's'}`,
      }))
    return (
      <PanelCard title={title}>
        {drawn.length === 0 ? (
          <Caption1 className={s.sub}>Nothing coded matches these filters.</Caption1>
        ) : (
          <HBarList rows={drawn} onSelect={onSelect} />
        )}
      </PanelCard>
    )
  }

  // NOT CLICKABLE, and the one spend panel that is not: an associated entity is
  // a vehicle, a staff member or a participant, and the criteria have no field
  // for one. A bar that looked like the three beside it and did nothing on click
  // would be worse than a bar that plainly is not a control.
  const entityPanel = useMemo(() => {
    if (data?.entities == null) return null
    const drawn: BarRowData[] = [...data.entities.entities]
      .filter((row) => row.total > 0)
      .sort((a, b) => b.total - a.total)
      .slice(0, TOP_N)
      .map((row) => ({
        key: `${row.entity_type}|${row.entity_key}`,
        label: row.entity_name ?? row.entity_key,
        total: row.total,
        display: money(row.total),
        segments: [{ color: SECTION_COLORS.spend, value: row.total }],
        title: `${row.entity_name ?? row.entity_key}: ${money(row.total)}`,
      }))
    return (
      <PanelCard title="Entities by Spend">
        {drawn.length === 0 ? (
          <Caption1 className={s.sub}>No tagged entities match these filters.</Caption1>
        ) : (
          <HBarList rows={drawn} />
        )}
      </PanelCard>
    )
  }, [data, s.sub])

  const correctionBars = useCallback(
    (rows: { key: string; label: string; value: number }[], color: string): BarRowData[] =>
      [...rows]
        .filter((row) => row.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, TOP_N)
        .map((row) => ({
          key: row.key,
          label: row.label,
          total: row.value,
          display: String(row.value),
          segments: [{ color, value: row.value }],
          title: `${row.label}: ${row.value}`,
        })),
    [],
  )

  /**
   * REQUIRED CORRECTIONS PER FLAG (D-368; renamed from Flags That Needed a Correction, owner 2026-10-16): per flag type, the share of its
   * RESOLVED flags that saw a person's correction on the same invoice — on the
   * same line, for a flag about one line — between being raised and being
   * resolved. The rest were fine and simply marked resolved. The server sends
   * them highest share first; the definition is each bar's tooltip, since a
   * panel here carries no caption.
   */
  const neededBars = useCallback(
    (rows: readonly { flag_type: string; resolved_count: number; corrected_count: number }[]): BarRowData[] =>
      rows
        .filter((row) => row.resolved_count > 0)
        .slice(0, TOP_N)
        .map((row) => {
          const percent = Math.round((row.corrected_count / row.resolved_count) * 100)
          const label = flagLabel(row.flag_type)
          return {
            key: row.flag_type,
            label,
            total: percent,
            display: `${percent}% of ${row.resolved_count}`,
            segments: [{ color: SECTION_COLORS.feedback, value: percent }],
            title:
              `${label}: ${row.corrected_count} of ${row.resolved_count} resolved flags saw a correction ` +
              'on their invoice (their line, for a line flag) between being raised and being resolved.',
          }
        }),
    [],
  )

  return (
    <div className={s.root}>
      {/* NO HEADER OF ITS OWN. Analytics carries two sub-tabs, so its title,
          subheader and the Filters button all sit above the tab bar in
          `AnalyticsScreen.tsx` — the References and Rules arrangement. */}

      {/* THE SAME SHAPE AS EVERY SECTION BELOW: heading outside, surface under
          it. NO CAPTION (owner, 2026-09-15) — it read "The whole queue — these
          four ignore the filters, week of …"; the heading and the four card
          labels already say what the block is, and the sentence was the longest
          line in it. The behaviour is unchanged — the endpoint still takes no
          parameters and refuses any it is given. */}
      <div className={s.section}>
        <div className={s.sectionHead}>
          <Subtitle2>This Week at a Glance</Subtitle2>
        </div>
        <div className={s.kpiBlock}>
        {kpiError !== null ? (
          <ErrorBanner message={kpiError} onRetry={() => setKpiRetry((n) => n + 1)} />
        ) : kpis === null ? (
          <Caption1 className={s.sub}>Loading…</Caption1>
        ) : (
          <div className={s.kpiGrid}>
            <KpiCard
              label="Invoices Imported This Week"
              value={String(kpis.imported_this_week.count)}
              sub={formatCurrency(kpis.imported_this_week.total)}
            />
            <KpiCard
              label="Invoices in Progress"
              value={String(kpis.in_progress.count)}
              sub={formatCurrency(kpis.in_progress.total)}
            />
            <KpiCard
              label="Invoices Past Due"
              value={String(kpis.past_due.count)}
              sub={`received over ${kpis.past_due_days} days ago · ${formatCurrency(kpis.past_due.total)}`}
              subDanger={kpis.past_due.count > 0}
            />
            <KpiCard
              label="Invoices Exported This Week"
              value={String(kpis.exported_this_week.count)}
              sub={formatCurrency(kpis.exported_this_week.total)}
            />
          </div>
        )}
        {/* UNDER the four, spanning all of them: the bar is what *Invoices In
            Progress* is made of, and sums to it exactly. It does NOT partition
            *Invoices Past Due* — same clock, different rounding; the component's
            own header says why. */}
        {kpis !== null && (
          <div className={s.kpiBar}>
            <AgeProgressBar
              bands={kpis.outstanding_by_age}
              unknown={kpis.outstanding_unknown_age}
            />
          </div>
        )}
        </div>
      </div>

      <ErrorBanner message={error} />
      {loading ? (
        <Loading label="Loading metrics…" />
      ) : data === null ? null : (
        <>
          {quad(
            'Per Step',
            data.step,
            stepLabel,
            'Step',
            SECTION_COLORS.step,
            // A step bar is keyed by pipeline NODE, and the Status control is
            // asked in node keys — except Archives, which expands to its eight
            // terminal statuses because the control offers them separately.
            selectFor('statuses', statusSelectionForNode),
          )}
          {quad('Per Vendor', data.vendor, plainLabel, 'Vendor', SECTION_COLORS.vendor, selectFor('vendors'))}
          {quad(
            'Per Company',
            data.company,
            plainLabel,
            'Company',
            SECTION_COLORS.company,
            selectFor('companies'),
          )}

          <div className={s.section}>
            <div className={s.sectionHead}>
              <Subtitle2>By Spend</Subtitle2>
            </div>
            <div className={s.quad}>
              {/* Account, Department and Code are the trailing half, the
                  leading half and the whole code the two compose
                  (`docs/decisions.md` D-85), so each bar toggles the criterion
                  of the same name. */}
              {spendPanel('Accounts by Spend', data.accounts, selectFor('accounts'))}
              {spendPanel('Departments by Spend', data.departments, selectFor('departments'))}
              {spendPanel('Codes by Spend', data.codes, selectFor('codes'))}
              {entityPanel}
            </div>
          </div>

          <div className={s.section}>
            <div className={s.sectionHead}>
              <Subtitle2>Corrections, Flags, and Holds</Subtitle2>
            </div>
            <div className={s.quad}>
              {data.corrections && (
                <PanelCard title="Corrections Per Vendor">
                  <HBarList
                    rows={correctionBars(
                      data.corrections.by_vendor.map((row) => ({
                        key: row.vendor_id ?? NO_KEY,
                        label: row.vendor_name ?? row.vendor_id ?? '(no vendor)',
                        value: row.count,
                      })),
                      SECTION_COLORS.feedback,
                    )}
                    onSelect={selectFor('vendors')}
                  />
                </PanelCard>
              )}
              {data.corrections && (
                <PanelCard title="Corrections Per Field">
                  {/* THE FIELD'S DISPLAY NAME, not its column name (owner,
                      2026-09-15). `correctionFieldLabel` is the one map, and it
                      is the same one Decision History and the old Correction
                      Rate panel read — so `account_id` reads *Account*
                      and `department_id` reads *Department*, exactly as the
                      pickers that produced the correction are labelled. A field
                      the map does not name keeps its stored spelling, which is
                      honest rather than blank. NOT CLICKABLE: there is no
                      corrected-field criterion to toggle. */}
                  <HBarList
                    rows={correctionBars(
                      data.corrections.by_field.map((row) => ({
                        key: row.field,
                        label: correctionFieldLabel(row.field),
                        value: row.count,
                      })),
                      SECTION_COLORS.feedback,
                    )}
                  />
                </PanelCard>
              )}
              {/* THE FOUR FLAG PANELS (owner, 2026-10-16 — D-368), between the
                  corrections and the holds, read from the same §16.6 response
                  and so under the same filters and the same role gate. Flags
                  Per Type and Required Corrections Per Flag keep the top-ten
                  rule every other ranking here follows. Neither is clickable:
                  there is no flag-type criterion to toggle. */}
              {data.corrections && (
                <PanelCard title="Flags Per Vendor">
                  <HBarList
                    rows={correctionBars(
                      data.corrections.flags.by_vendor.map((row) => ({
                        key: row.vendor_id ?? NO_KEY,
                        label: row.vendor_name ?? row.vendor_id ?? '(no vendor)',
                        value: row.count,
                      })),
                      SECTION_COLORS.feedback,
                    )}
                    onSelect={selectFor('vendors')}
                  />
                </PanelCard>
              )}
              {data.corrections && (
                <PanelCard title="Median Flags Per Invoice">
                  <HBarList rows={weeklyMedianBars(data.corrections.flags.median_by_week, SECTION_COLORS.feedback)} />
                </PanelCard>
              )}
              {data.corrections && (
                <PanelCard title="Flags Per Type">
                  <HBarList
                    rows={correctionBars(
                      data.corrections.flags.by_type.map((row) => ({
                        key: row.flag_type,
                        label: flagLabel(row.flag_type),
                        value: row.count,
                      })),
                      SECTION_COLORS.feedback,
                    )}
                  />
                </PanelCard>
              )}
              {data.corrections && (
                <PanelCard title="Required Corrections Per Flag">
                  <HBarList rows={neededBars(data.corrections.flags.needed_correction_by_type)} valueWidth={88} />
                </PanelCard>
              )}
              {data.vendor && (
                <PanelCard title="Holds Per Vendor">
                  <HBarList
                    rows={bars(data.vendor.rows, holds, whole, SECTION_COLORS.feedback, plainLabel)}
                    onSelect={selectFor('vendors')}
                  />
                </PanelCard>
              )}
              {data.step && (
                <PanelCard title="Holds Per Step">
                  <HBarList
                    rows={bars(data.step.rows, holds, whole, SECTION_COLORS.feedback, stepLabel)}
                    onSelect={selectFor('statuses', statusSelectionForNode)}
                  />
                </PanelCard>
              )}
            </div>
          </div>

          <PanelCard title="Invoices Included in This View">
            {rows !== null && rows.length === 0 && !pageLoading ? (
              <div className={s.empty}>
                <Body1>No invoices match these filters.</Body1>
              </div>
            ) : (
              <>
                {/* THE TABLE IS NEVER UNMOUNTED BY A PAGE TURN (owner,
                    2026-09-15). It holds the previous page's rows until the
                    next page arrives and only dims, so the panel keeps its
                    height and the pager does not jump up the screen and back
                    down again. */}
                <Table
                  size="small"
                  className={mergeClasses(s.listTable, pageLoading && s.listLoading)}
                  aria-busy={pageLoading}
                >
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell style={{ width: '20%' }}>Company</TableHeaderCell>
                      <TableHeaderCell style={{ width: '22%' }}>Vendor</TableHeaderCell>
                      <TableHeaderCell style={{ width: '14%' }}>Date</TableHeaderCell>
                      <TableHeaderCell style={{ width: '8%' }}>Age</TableHeaderCell>
                      <TableHeaderCell style={{ width: '24%' }}>Status</TableHeaderCell>
                      <TableHeaderCell style={{ width: '12%' }} className={s.rightHeader}>
                        Total
                      </TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  {/* A ROW OPENS THE INVOICE, read-only (owner, 2026-09-14).
                      `rowActivationProps` carries the keyboard path too, so the
                      row is the control rather than a click target. */}
                  <TableBody>
                    {(rows ?? []).map((inv) => (
                      <TableRow
                        key={inv.id}
                        className={clickableRow}
                        {...rowActivationProps(
                          () => setOpenInvoice(inv),
                          `${inv.vendor_name ?? 'Invoice'} — ${inv.company_name ?? 'no company'}`,
                        )}
                      >
                        <TableCell className={s.ellipsis}>{inv.company_name ?? '—'}</TableCell>
                        <TableCell className={s.ellipsis}>{inv.vendor_name ?? '—'}</TableCell>
                        <TableCell>{formatDate(inv.invoice_date)}</TableCell>
                        <TableCell>
                          <AgePill days={ageInDays(inv.invoice_date)} />
                        </TableCell>
                        <TableCell className={s.ellipsis}>
                          <StatusBadge status={inv.status} />
                        </TableCell>
                        <TableCell className={s.right}>{formatCurrency(inv.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className={s.pager}>
                  <Button
                    size="small"
                    disabled={pageIndex === 0 || pageLoading}
                    onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    disabled={nextCursor === undefined || pageLoading}
                    onClick={showMore}
                  >
                    Next
                  </Button>
                  {/* "Page 3 of 5" (owner, 2026-09-15). The denominator comes
                      from the server's own total for the filtered set; with no
                      total it says which page this is and claims nothing about
                      how many there are. */}
                  <Caption1>
                    {pageCount === null
                      ? `Page ${pageIndex + 1}`
                      : `Page ${pageIndex + 1} of ${pageCount}`}
                    {total === null ? '' : ` · ${total} invoice${total === 1 ? '' : 's'}`}
                  </Caption1>
                </div>
              </>
            )}
          </PanelCard>

          {/* No `editStep` and no `onReleased`/`onArchived`: this is the
              read-only view, and the modal offers no action bar without
              them. `screen="archives"` is the same read-only line-item
              presentation Archives asks for — the Dashboard's rows are the
              same mixed bag of statuses. */}
          <InvoiceDetailModal
            open={!!openInvoice}
            invoice={openInvoice}
            showPdf={showPdf}
            user={session.user}
            viewOnly
            screen="archives"
            // Closes on the way out: the queue screen it hands over to is
            // behind this overlay, and leaving the modal up over it would put
            // the same invoice on screen twice, read-only over editable.
            resolveStep={
              resolveStep
                ? (inv) => {
                    const target = resolveStep(inv)
                    return target
                      ? {
                          label: target.label,
                          onGo: () => {
                            setOpenInvoice(null)
                            target.onGo()
                          },
                        }
                      : null
                  }
                : undefined
            }
            onClose={() => setOpenInvoice(null)}
          />
        </>
      )}
    </div>
  )
}
